import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getRace, getDrivers, getEntries, submitEntry, getRaceLeaderboard, getRaceQualifying, getRaceStartingGrid } from '../api';
import { Link } from 'react-router-dom';
import RaceBadge from '../components/RaceBadge';
import DriverSelect from '../components/DriverSelect';
import ScoreTable from '../components/ScoreTable';
import RaceCodeGate from '../components/RaceCodeGate';
import VideoOverlay from '../components/VideoOverlay';
import RacePodium from '../components/RacePodium';
import { getStoredUser, setStoredUser, getStoredEmail, setStoredEmail, getStoredRaceCode, clearStoredRaceCode, hasSeenPodium, markPodiumSeen } from '../user';

const s = {
  header: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 },
  h1: { fontSize: 28, fontWeight: 700, fontFamily: 'var(--font-display)', letterSpacing: '0.02em', textTransform: 'uppercase' },
  meta: { color: '#999', fontSize: 13, marginBottom: 24 },
  card: { background: '#1a1a1a', borderRadius: 8, padding: 24, marginBottom: 24 },
  label: { fontSize: 12, color: '#999', textTransform: 'uppercase', fontWeight: 600, marginBottom: 6, display: 'block' },
  input: {
    width: '100%', padding: '10px 12px', background: '#111',
    border: '1px solid #333', borderRadius: 4, color: '#f0f0f0', fontSize: 14, marginBottom: 16,
  },
  btn: {
    padding: '10px 24px', background: '#f9b000', border: 'none', borderRadius: 4,
    color: '#111', fontWeight: 600, cursor: 'pointer', fontSize: 14,
  },
  btnDisabled: { opacity: 0.5, cursor: 'not-allowed' },
  resultRow: { display: 'flex', gap: 8, marginBottom: 4, fontSize: 14, alignItems: 'center', borderRadius: 3, paddingLeft: 10 },
  pos: { color: '#f9b000', fontWeight: 700, width: 32, fontFamily: 'var(--font-mono)', fontSize: 13 },
  h2: { fontSize: 18, fontWeight: 700, marginBottom: 16 },
  success: { background: '#14532d', borderRadius: 6, padding: 16, color: '#86efac' },
  err: { color: '#f9b000', fontSize: 13, marginTop: 8 },
};

// ── Qualifying Grid ────────────────────────────────────────────────────────────
const qualKf = `
  @keyframes qualRowIn {
    from { opacity: 0; transform: translateX(-3px); }
    to   { opacity: 1; transform: translateX(0); }
  }
`;

function QualifyingGrid({ grid, drivers }) {
  const [open, setOpen] = useState(true);
  const [showEliminated, setShowEliminated] = useState(false);

  const colourByCode = {};
  for (const d of drivers) {
    if (d.acronym && d.teamColour) colourByCode[d.acronym] = d.teamColour;
  }

  const bestTime = (row) => row.q3 || row.q2 || row.q1 || '—';

  const rows = grid.map(row => {
    const isQ3 = row.position <= 10;
    const isQ2 = row.position >= 11 && row.position <= 15;
    const colour = colourByCode[row.code];
    const stripeColor = colour ? `#${colour}` : '#444';
    return { ...row, isQ3, isQ2, colour: stripeColor };
  });

  const q3rows = rows.filter(r => r.isQ3);
  const q2rows = rows.filter(r => r.isQ2);
  const q1rows = rows.filter(r => !r.isQ3 && !r.isQ2);

  const renderRow = (row, i, dimLevel) => (
    <div key={row.position} style={{
      display: 'flex', alignItems: 'center',
      borderLeft: `3px solid ${row.colour}`,
      borderBottom: '1px solid rgba(255,255,255,0.025)',
      padding: '0 12px 0 10px',
      height: 36,
      background: row.position === 1 ? 'rgba(249,176,0,0.03)' : 'transparent',
      boxShadow: row.position === 1 ? 'inset 3px 0 10px rgba(249,176,0,0.07)' : 'none',
      animation: open ? 'qualRowIn 0.25s ease both' : 'none',
      animationDelay: `${i * 18}ms`,
    }}>
      {/* Position */}
      <span style={{
        fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 13,
        color: dimLevel === 0 ? '#f9b000' : '#444',
        width: 26, textAlign: 'right', flexShrink: 0, marginRight: 10,
      }}>
        {row.position}
      </span>

      {/* Code */}
      <span style={{
        fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 13,
        color: '#f0f0f0', letterSpacing: '0.06em', flexShrink: 0, marginRight: 8, width: 36,
      }}>
        {row.code}
      </span>

      {/* Full name */}
      <span style={{
        fontSize: 12, color: '#555', flex: 1,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
        {row.fullName}
      </span>

      {/* Best time */}
      <span style={{
        fontFamily: 'var(--font-mono)', fontSize: 12,
        color: row.position === 1 ? '#f9b000' : '#777',
        flexShrink: 0, minWidth: 72, textAlign: 'right',
      }}>
        {bestTime(row)}
      </span>
    </div>
  );

  const Divider = ({ label }) => (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      padding: '5px 12px', borderBottom: '1px solid rgba(255,255,255,0.025)',
    }}>
      <div style={{ flex: 1, height: 1, background: '#1e1e1e' }} />
      <span style={{
        fontFamily: 'var(--font-mono)', fontSize: 9,
        color: '#2a2a2a', letterSpacing: '0.12em', flexShrink: 0,
      }}>
        {label}
      </span>
      <div style={{ flex: 1, height: 1, background: '#1e1e1e' }} />
    </div>
  );

  return (
    <div style={{ background: '#1a1a1a', borderRadius: 8, marginBottom: 24, overflow: 'hidden' }}>
      <style>{qualKf}</style>

      {/* Header */}
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '10px 14px', background: '#111', border: 'none', borderBottom: open ? '1px solid #1e1e1e' : 'none',
          cursor: 'pointer', textAlign: 'left',
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontFamily: 'var(--font-display)', fontSize: 11, color: '#555', textTransform: 'uppercase', letterSpacing: '0.14em' }}>Qualifying</span>
          <span style={{ fontFamily: 'var(--font-display)', fontSize: 13, fontWeight: 700, color: '#f9b000', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Grid</span>
        </span>
        <span style={{
          fontFamily: 'var(--font-mono)', fontSize: 11, color: '#444',
          transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
          transition: 'transform 0.2s ease', display: 'inline-block',
        }}>›</span>
      </button>

      {/* Rows */}
      {open && (
        <div>
          {/* Q3 — always visible, with fade hint when eliminated is hidden */}
          <div style={{ position: 'relative' }}>
            {q3rows.map((row, i) => renderRow(row, i, 0))}
            {!showEliminated && (q2rows.length > 0 || q1rows.length > 0) && (
              <div style={{
                position: 'absolute', bottom: 0, left: 0, right: 0, height: 40,
                background: 'linear-gradient(to bottom, transparent, #1a1a1a)',
                pointerEvents: 'none',
              }} />
            )}
          </div>

          {/* Expand toggle — styled as a timing-sheet row */}
          {(q2rows.length > 0 || q1rows.length > 0) && (
            <button
              onClick={() => setShowEliminated(s => !s)}
              style={{
                width: '100%', display: 'flex', alignItems: 'center',
                background: showEliminated ? 'transparent' : 'rgba(255,255,255,0.01)',
                border: 'none', borderLeft: '3px solid #252525',
                borderBottom: '1px solid rgba(255,255,255,0.025)',
                padding: '0 12px 0 10px', height: 30,
                cursor: 'pointer', gap: 0,
              }}
            >
              <span style={{ width: 26, flexShrink: 0, marginRight: 10 }} />
              <span style={{
                fontFamily: 'var(--font-mono)', fontSize: 10,
                color: '#444', letterSpacing: '0.1em', flex: 1, textAlign: 'left',
              }}>
                {showEliminated
                  ? 'HIDE ELIMINATED'
                  : `Q2 · Q1 — ${q2rows.length + q1rows.length} MORE DRIVERS`}
              </span>
              <span style={{
                fontSize: 9, color: '#555',
                transition: 'color 0.15s',
              }}>
                {showEliminated ? '▲' : '▼'}
              </span>
            </button>
          )}

          {/* Q2 + Q1 — revealed on expand */}
          {showEliminated && <>
            {q2rows.length > 0 && <>
              <Divider label="Q2 ELIMINATED" />
              {q2rows.map((row, i) => renderRow(row, q3rows.length + i, 1))}
            </>}
            {q1rows.length > 0 && <>
              <Divider label="Q1 ELIMINATED" />
              {q1rows.map((row, i) => renderRow(row, q3rows.length + q2rows.length + i, 2))}
            </>}
          </>}
        </div>
      )}
    </div>
  );
}

export default function RaceDetailPage() {
  const { id } = useParams();
  const [race, setRace] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [driversFallback, setDriversFallback] = useState(false);
  const [entries, setEntries] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [qualifyingGrid, setQualifyingGrid] = useState([]);
  const [startingGrid, setStartingGrid] = useState(null);

  const [name, setName] = useState(getStoredUser);
  const [email, setEmail] = useState(getStoredEmail);
  const [predictions, setPredictions] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const [error, setError] = useState(null);
  const [codeGateError, setCodeGateError] = useState(null);
  const [codeVerified, setCodeVerified] = useState(() => !!getStoredRaceCode(id));
  const [showVideo, setShowVideo] = useState(false);
  const [showPodium, setShowPodium] = useState(false);

  useEffect(() => {
    let active = true;
    getRaceStartingGrid(id).then(rows => {
      if (active) setStartingGrid({ raceId: String(id), rows: Array.isArray(rows) ? rows : [] });
    }).catch(() => {});
    return () => { active = false; };
  }, [id]);

  useEffect(() => {
    Promise.all([
      getRace(id),
      getDrivers(id).catch(() => ({ drivers: [], isFallback: false })),
      getEntries(id).catch(() => []),
      getRaceLeaderboard(id).catch(() => []),
      getRaceQualifying(id).catch(() => []),
    ]).then(([r, d, e, lb, qual]) => {
      setRace(r);
      // Handle both array (old format) and {drivers, isFallback} (new format)
      const driversData = Array.isArray(d) ? d : (d.drivers || []);
      const fallback = Array.isArray(d) ? false : (d.isFallback || false);
      setDrivers(driversData);
      setDriversFallback(fallback);
      setEntries(e);
      setLeaderboard(lb);
      setQualifyingGrid(qual || []);
      if (r.official_top10 && lb.length > 0 && !hasSeenPodium(id)) {
        setShowPodium(true);
        markPodiumSeen(id);
      }
    }).catch(err => {
      setError(err.message || 'Failed to load race');
    }).finally(() => setLoading(false));
  }, [id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (predictions.length !== 10) {
      return setError('Please select exactly 10 drivers');
    }
    setSubmitting(true);
    try {
      const result = await submitEntry(id, { user_name: name, email, predictions, entry_code: getStoredRaceCode(id) });
      setStoredUser(name);
      setStoredEmail(email);
      setSubmitted(result);
    } catch (err) {
      const msg = err.response?.data?.error || err.message;
      if (err.response?.status === 403) {
        clearStoredRaceCode(id);
        setCodeVerified(false);
        setCodeGateError('Incorrect code — please try again');
      } else {
        setError(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <p style={{ color: '#666' }}>Loading...</p>;
  if (error && !race) return <p style={{ color: '#f9b000' }}>Error: {error}</p>;
  if (!race) return <p style={{ color: '#f9b000' }}>Race not found</p>;

  const hasResults = !!race.official_top10;
  const currentUser = getStoredUser();

  return (
    <div>
      {showVideo && race.video_url && (
        <VideoOverlay url={race.video_url} onClose={() => setShowVideo(false)} />
      )}
      {showPodium && (
        <RacePodium race={race} leaderboard={leaderboard.filter(r => !r.is_staff)} onClose={() => setShowPodium(false)} />
      )}
      <div style={s.header}>
        <h1 style={s.h1}>{race.name}</h1>
        <RaceBadge race={race} />
      </div>
      <p style={s.meta}>
        Round {race.round}
        {race.country ? ` · ${race.country}` : ''}
        {race.race_date ? ` · ${new Date(race.race_date).toLocaleDateString()}` : ''}
      </p>
      {race.video_url && (
        <button
          onClick={() => setShowVideo(true)}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            marginBottom: 20,
            background: '#e8002d', border: 'none', borderRadius: 5,
            color: '#fff', fontFamily: 'var(--font-mono)', fontSize: 12,
            fontWeight: 700, letterSpacing: '0.1em', padding: '9px 18px',
            cursor: 'pointer',
          }}
        >
          ▶ PLAY VIDEO MESSAGE
        </button>
      )}

      {hasResults && (() => {
        const myRow = currentUser && leaderboard.find(r => r.user_name === currentUser);
        if (!myRow) return null;
        const hits = Object.values(myRow.breakdown || {}).filter(p => p > 0).length;
        return (
          <div style={{ ...s.card, background: myRow.score > 0 ? '#14532d' : '#1a1a1a', marginBottom: 16 }}>
            <p style={{ fontSize: 12, color: myRow.score > 0 ? '#86efac' : '#999', textTransform: 'uppercase', fontWeight: 600, marginBottom: 4 }}>Your Score</p>
            <p style={{ fontSize: 32, fontWeight: 700, color: myRow.score > 0 ? '#86efac' : '#f0f0f0', margin: 0 }}>
              {myRow.score} pts
            </p>
            <p style={{ fontSize: 13, color: myRow.score > 0 ? '#86efac' : '#666', marginTop: 4 }}>
              {hits} correct position{hits !== 1 ? 's' : ''} out of 10
            </p>
          </div>
        );
      })()}

      {hasResults && (
        <div style={s.card}>
          <h2 style={s.h2}>Official Results</h2>
          {race.official_top10.map((acr, i) => {
            const d = drivers.find(dr => dr.acronym === acr);
            const podiumColor = ['#FFD700', '#C0C0C0', '#CD7F32'][i] ?? null;
            const teamColor = d?.teamColour ? `#${d.teamColour}` : '#444';
            return (
              <div key={i} style={{ ...s.resultRow, borderLeft: `3px solid ${teamColor}`, background: i < 3 ? 'rgba(255,255,255,0.025)' : 'transparent', marginBottom: 6 }}>
                <span style={{ ...s.pos, color: podiumColor ?? '#f9b000' }}>P{i + 1}</span>
                <span style={{ fontWeight: 700, fontFamily: 'var(--font-mono)', letterSpacing: '0.04em', fontSize: 13 }}>{acr}</span>
                {d && <span style={{ color: '#aaa', fontSize: 13 }}>{d.fullName}</span>}
              </div>
            );
          })}
        </div>
      )}

      {hasResults && leaderboard.length > 0 && (
        <div style={s.card}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <h2 style={{ ...s.h2, marginBottom: 0 }}>Race Scores</h2>
            <button
              onClick={() => setShowPodium(true)}
              style={{
                background: 'transparent', border: '1px solid rgba(249,176,0,0.3)', borderRadius: 5,
                color: '#f9b000', fontFamily: 'var(--font-mono)', fontSize: 11,
                fontWeight: 700, letterSpacing: '0.08em', padding: '6px 12px',
                cursor: 'pointer',
              }}
            >
              🏆 VIEW PODIUM
            </button>
          </div>
          <ScoreTable rows={leaderboard.filter(r => !r.is_staff)} currentUser={currentUser} />
        </div>
      )}

      {qualifyingGrid.length > 0 && (
        <QualifyingGrid grid={qualifyingGrid} drivers={drivers} />
      )}

      {race.cancelled && (
        <div style={{ padding: '18px 20px', background: '#1a0a0a', border: '1px solid #3a1a1a', borderRadius: 6, marginBottom: 16 }}>
          <p style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: '#ef4444', letterSpacing: '0.1em', textTransform: 'uppercase', margin: 0 }}>
            This race has been cancelled.
          </p>
        </div>
      )}

      {!race.cancelled && race.locked && !hasResults && (
        <div style={{ ...s.card, color: '#f59e0b' }}>
          Race is locked — awaiting results.
        </div>
      )}

      {!race.cancelled && !race.locked && !hasResults && !race.code_required && (
        <div style={{ ...s.card, borderLeft: '4px solid #333', color: '#666' }}>
          <p style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: '#444', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 8 }}>
            Predictions not open yet
          </p>
          <p style={{ fontSize: 14, margin: 0 }}>
            The race code will be shared closer to race weekend. Check back then to submit your prediction.
          </p>
        </div>
      )}

      {!race.cancelled && !race.locked && !hasResults && race.code_required && !codeVerified && (
        <RaceCodeGate
          race={race}
          onVerified={(code) => { setCodeGateError(null); setCodeVerified(true); }}
          error={codeGateError}
        />
      )}

      {!race.cancelled && !race.locked && !hasResults && race.code_required && codeVerified && (
        <div style={s.card}>
          <h2 style={s.h2}>Submit Your Prediction</h2>
          {submitted ? (
            <div style={s.success}>
              <strong>{submitted.is_new === false ? 'Prediction updated!' : 'Prediction submitted!'}</strong>
              <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 4 }}>
                {submitted.predictions.map((acr, i) => {
                  const d = drivers.find(dr => dr.acronym === acr);
                  return (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                      <span style={{ fontFamily: 'var(--font-mono)', color: '#86efac', opacity: 0.6, width: 28, flexShrink: 0 }}>P{i + 1}</span>
                      <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700 }}>{acr}</span>
                      {d && <span style={{ color: '#86efac', opacity: 0.8 }}>{d.fullName}</span>}
                    </div>
                  );
                })}
              </div>
              <p style={{ marginTop: 12, fontSize: 12, color: '#86efac', opacity: 0.7 }}>
                Changed your mind? Submit again before the race locks to update your prediction.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <label style={s.label}>Your name</label>
              <input
                style={s.input}
                value={name}
                onChange={e => setName(e.target.value)}
                required
                placeholder="Name (used as unique identifier)"
              />
              <label style={s.label}>Email</label>
              <input
                style={s.input}
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                placeholder="you@example.com"
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <span style={{ fontSize: 12, color: '#999', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.04em' }}>Pick your Top 10</span>
                <Link to="/drivers" style={{ fontSize: 11, color: '#f9b000', textDecoration: 'none', fontFamily: 'var(--font-mono)' }}>Not sure who's racing? →</Link>
              </div>

              {/* How it works strip */}
              <div style={{
                background: '#161616',
                border: '1px solid #2a2a2a',
                borderRadius: 6,
                padding: '14px 16px',
                marginBottom: 16,
              }}>
                <div style={{ display: 'flex', gap: 0 }}>
                  {[
                    { n: '01', title: 'Pick your top 10', desc: 'Click to add, drag to reorder' },
                    { n: '02', title: 'Race happens', desc: 'Predictions lock at race start' },
                    { n: '03', title: 'Score points', desc: 'Exact position = F1 points' },
                  ].map((step, i) => (
                    <React.Fragment key={step.n}>
                      {i > 0 && <div style={{ width: 1, background: '#2a2a2a', margin: '0 16px', flexShrink: 0 }} />}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 700, color: '#f9b000', marginBottom: 3 }}>{step.n}</div>
                        <div style={{ fontFamily: 'var(--font-display)', fontSize: 14, fontWeight: 600, color: '#f0f0f0', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 2 }}>{step.title}</div>
                        <div style={{ fontSize: 11, color: '#666' }}>{step.desc}</div>
                      </div>
                    </React.Fragment>
                  ))}
                </div>
                <div style={{ borderTop: '1px solid #2a2a2a', marginTop: 12, paddingTop: 10, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  {[25,18,15,12,10,8,6,4,2,1].map((pts, i) => (
                    <span key={i} style={{
                      fontFamily: 'var(--font-mono)',
                      fontSize: 11,
                      padding: '2px 6px',
                      borderRadius: 3,
                      background: '#1a1a1a',
                      border: `1px solid ${i === 0 ? 'rgba(249,176,0,0.3)' : '#333'}`,
                      color: i === 0 ? '#f9b000' : '#555',
                    }}>
                      P{i + 1} {pts}
                    </span>
                  ))}
                </div>
              </div>

              {driversFallback && (
                <p style={{ fontSize: 12, color: '#f59e0b', marginBottom: 10 }}>
                  ⚠ Live driver data unavailable — showing cached or default 2026 grid. Verify the lineup before submitting.
                </p>
              )}
              {drivers.length === 0 && (
                <p style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: '#555', marginBottom: 10 }}>
                  ⏳ Driver data not yet available from OpenF1. Check back closer to race weekend.
                </p>
              )}
              <DriverSelect
                drivers={drivers}
                qualifyingGrid={qualifyingGrid}
                startingGrid={startingGrid?.raceId === String(id) ? startingGrid.rows : []}
                value={predictions}
                onChange={setPredictions}
              />
              {error && <p style={s.err}>{error}</p>}
              <p style={{ fontSize: 12, color: '#666', marginTop: 16 }}>
                Your name will appear publicly in the leaderboard. Your email is used for event
                communications only. See our{' '}
                <Link to="/privacy" style={{ color: '#999' }}>Privacy Policy</Link>.
              </p>
              <button
                type="submit"
                style={{ ...s.btn, marginTop: 12, ...(submitting ? s.btnDisabled : {}) }}
                disabled={submitting}
              >
                {submitting ? 'Submitting…' : 'Submit Prediction'}
              </button>
            </form>
          )}
        </div>
      )}

      {entries.length > 0 && (
        <div style={s.card}>
          <h2 style={s.h2}>Entries ({entries.length})</h2>
          {!race.locked && (
            <p style={{ fontSize: 13, color: '#666', marginBottom: 12 }}>
              Predictions are hidden until the race is locked.
            </p>
          )}
          <div className="table-scroll">
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '6px 8px', color: '#999', borderBottom: '1px solid #333' }}>Name</th>
                {race.locked && <th style={{ textAlign: 'left', padding: '6px 8px', color: '#999', borderBottom: '1px solid #333' }}>P1 → P10</th>}
                <th style={{ textAlign: 'right', padding: '6px 8px', color: '#999', borderBottom: '1px solid #333' }}>Submitted</th>
              </tr>
            </thead>
            <tbody>
              {entries.map(e => {
                const isMe = currentUser && e.user_name === currentUser;
                return (
                  <tr key={e.id} style={isMe ? { background: '#1c1a0e' } : {}}>
                    <td style={{ padding: '8px 8px', borderBottom: '1px solid #1a1a1a', fontWeight: isMe ? 700 : 400, color: isMe ? '#f9b000' : 'inherit' }}>
                      {e.user_name}{isMe && ' ★'}
                    </td>
                    {race.locked && (
                      <td style={{ padding: '8px 8px', borderBottom: '1px solid #1a1a1a', color: '#aaa' }}>
                        {e.predictions.join(' · ')}
                      </td>
                    )}
                    <td style={{ padding: '8px 8px', borderBottom: '1px solid #1a1a1a', textAlign: 'right', color: '#666' }}>
                      {new Date(e.submitted_at).toLocaleString()}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
      )}
    </div>
  );
}
